import React, { useState, useCallback, useMemo, memo } from "react";
import { useRouter } from "next/navigation";
import { AWS_URL } from "@/constants";
import { ChevronRight, X, ArrowRight, Sparkles } from "lucide-react";

// ── NavLink: a real <a> so ctrl/cmd/middle-click "open in new tab" works,
//    while a plain left-click still runs client-side router.push + onNavigate ──
const NavLink = memo(
  ({ href, onClick, onMouseEnter, onMouseLeave, className, style, children }) => (
    <a
      href={href}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => {
        // Let the browser handle new-tab / new-window / middle-click natively.
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button === 1) {
          return;
        }
        e.preventDefault();
        onClick?.();
      }}
      className={className}
      style={style}
    >
      {children}
    </a>
  )
);
NavLink.displayName = "NavLink";

// ── Memoized sub-category card for tablet grid (prevents re-renders) ──
const SubCategoryCard = memo(({ subCat, href, onClick, imgUrl }) => (
  <NavLink
    href={href}
    onClick={onClick}
    className="group flex flex-col overflow-hidden rounded-sm border border-[#e8ddd4] hover:border-[#c9a96e] transition-colors duration-200 bg-[#faf7f4]"
  >
    <div className="relative w-full aspect-square bg-[#f5ede3] overflow-hidden">
      {imgUrl ? (
        <img
          src={imgUrl}
          alt={subCat.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover will-change-transform group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-[#c9a96e] opacity-30">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
            </svg>
          </span>
        </div>
      )}
      <div className="absolute inset-0 bg-[#1a1410]/0 group-hover:bg-[#1a1410]/10 transition-colors duration-200" />
    </div>
    <div className="px-1.5 py-1.5">
      <p className="text-[10px] text-[#6b6560] group-hover:text-[#1a1410] transition-colors leading-tight text-left line-clamp-2" style={{ fontFamily: "'Jost', sans-serif" }}>
        {subCat.name}
      </p>
    </div>
  </NavLink>
));
SubCategoryCard.displayName = "SubCategoryCard";

// ── Memoized desktop sub-category link ──
const SubCategoryLink = memo(({ subCat, href, onMouseEnter, onMouseLeave, onClick }) => (
  <li onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className="group">
    <NavLink
      href={href}
      onClick={onClick}
      className="flex items-center gap-2 text-xs xl:text-sm text-[#6b6560] group-hover:text-[#1a1410] transition-all duration-200 py-1.5 text-left w-full"
    >
      <span className="w-0 group-hover:w-3 overflow-hidden transition-all duration-200 flex-shrink-0">
        <span className="block w-3 h-px bg-[#c9a96e]" />
      </span>
      <span className="group-hover:translate-x-0.5 transition-transform duration-200 leading-relaxed">
        {subCat.name}
      </span>
    </NavLink>
  </li>
));
SubCategoryLink.displayName = "SubCategoryLink";

export default function MegaMenu({ categories, onNavigate }) {
  const router = useRouter();
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredSubCategory, setHoveredSubCategory] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!categories || categories.length === 0) return null;

  const displayCategory = hoveredCategory ?? categories[0];
  const allSubCategories = displayCategory?.sub_categories ?? [];

  // ── Helpers (memoized) ────────────────────────────────────────────────────

  const getProductUrl = useCallback((data) => {
    if (data?.path && AWS_URL) return `${AWS_URL}${data.path}`;
    return "";
  }, []);

  const getSubCategoryImageUrl = useCallback(
    (subCat) => getProductUrl(subCat?.fileByImageId ?? subCat?.file),
    [getProductUrl]
  );

  // Memoize hero image so it doesn't recompute on every mouse event
  const heroImage = useMemo(() => {
    if (hoveredSubCategory) {
      const url = getSubCategoryImageUrl(hoveredSubCategory);
      if (url) return url;
    }
    if (hoveredCategory?.file) {
      const url = getProductUrl(hoveredCategory.file);
      if (url) return url;
    }
    if (allSubCategories[0]) {
      const url = getSubCategoryImageUrl(allSubCategories[0]);
      if (url) return url;
    }
    if (displayCategory?.file) {
      const url = getProductUrl(displayCategory.file);
      if (url) return url;
    }
    return "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=800&q=80";
  }, [hoveredSubCategory, hoveredCategory, displayCategory, allSubCategories, getProductUrl, getSubCategoryImageUrl]);

  const heroTitle = hoveredSubCategory?.name ?? displayCategory?.name ?? "";

  const heroLink = useMemo(() =>
    hoveredSubCategory
      ? `/shop/${displayCategory.id}/${hoveredSubCategory.id}`
      : `/category/${displayCategory?.id}`,
    [hoveredSubCategory, displayCategory]
  );

  // ── Navigation (stable refs) ──────────────────────────────────────────────

  const navigate = useCallback((path) => {
    onNavigate?.();
    router.push(path);
  }, [onNavigate, router]);

  const slugify = (text) => {
    return text
      .toLowerCase()           // "Silver Rings" → "silver rings"
      .trim()                  // Remove leading/trailing spaces
      .replace(/\s+/g, '-')    // "silver rings" → "silver-rings" ✓
      .replace(/[^\w-]/g, ''); // Remove special characters
  };

  const getCategoryHref = useCallback((category) => `/category/${slugify(category.name)}`, []);
  const getSubCategoryHref = useCallback(
    (categoryId, subCat) => `/shop/${categoryId}/${subCat.id}`,
    []
  );
  const getViewAllHref = useCallback(
    (category) => `/shop-category/${category?.id}`,
    []
  );

  const handleCategoryClick = useCallback((category) =>
    navigate(getCategoryHref(category)),
    [navigate, getCategoryHref]
  );

  const handleSubCategoryClick = useCallback((subCat) =>
    navigate(`/shop/${displayCategory.id}/${subCat.id}`),
    [navigate, displayCategory]
  );

  const handleViewAllClick = useCallback(
    () => navigate(getViewAllHref(displayCategory)),
    [navigate, getViewAllHref, displayCategory]
  );

  const handleHeroClick = useCallback(() => navigate(heroLink), [navigate, heroLink]);

  const toggleCategoryExpand = useCallback((id) =>
    setExpandedCategory((prev) => (prev === id ? null : id)),
    []
  );

  const handleCategoryHover = useCallback((category) => {
    setHoveredCategory(category);
    setHoveredSubCategory(null);
  }, []);

  // ── Column split (memoized) ───────────────────────────────────────────────

  const [col1, col2, col3] = useMemo(() => {
    const itemsPerColumn = Math.ceil(allSubCategories.length / 3);
    return [
      allSubCategories.slice(0, itemsPerColumn),
      allSubCategories.slice(itemsPerColumn, itemsPerColumn * 2),
      allSubCategories.slice(itemsPerColumn * 2),
    ];
  }, [allSubCategories]);

  // Pre-compute all tablet image URLs to avoid recalculation in render
  const tabletImageUrls = useMemo(() =>
    Object.fromEntries(allSubCategories.map((s) => [s.id, getSubCategoryImageUrl(s)])),
    [allSubCategories, getSubCategoryImageUrl]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ════════════════ DESKTOP ════════════════ */}
      <div className="hidden lg:block w-full z-40" style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');
          .mega-menu-root { font-family: 'Jost', sans-serif; }
          .mega-menu-display { font-family: 'Cormorant Garamond', Georgia, serif; }
          .hero-img { transition: opacity 0.4s ease; }
          .cat-pill { position: relative; }
          .cat-pill::before {
            content: '';
            position: absolute;
            left: 0; top: 50%;
            transform: translateY(-50%);
            width: 2px; height: 0;
            background: #c9a96e;
            transition: height 0.25s ease;
            border-radius: 1px;
          }
          .cat-pill.active::before, .cat-pill:hover::before { height: 60%; }
          .subcol-divider {
            height: 1px;
            background: linear-gradient(90deg, #e8ddd4, transparent);
            margin-bottom: 1.5rem;
          }
          .view-all-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: #1a1410;
            font-family: 'Jost', sans-serif;
            font-weight: 500;
            padding: 8px 20px;
            border: 1px solid #c9a96e;
            transition: all 0.25s ease;
            white-space: nowrap;
          }
          .view-all-link:hover { background: #1a1410; color: #f5ede3; border-color: #1a1410; }
          .scrollbar-luxury::-webkit-scrollbar { width: 3px; }
          .scrollbar-luxury::-webkit-scrollbar-track { background: transparent; }
          .scrollbar-luxury::-webkit-scrollbar-thumb { background: #c9a96e; border-radius: 2px; }
          /* Tablet grid: promote cards to own layer to isolate paint */
          .tablet-card { contain: layout style paint; }
        `}</style>

        <div className="mega-menu-root w-full shadow-2xl" style={{ boxShadow: "0 20px 60px rgba(26,20,16,0.12)" }}>
          {/*
           * The outer flex row uses overflow-hidden so no child can bleed
           * outside its own box into adjacent siblings.
           * Each flex child also declares min-w-0 so text/content columns
           * never get displaced by the image panel growing unexpectedly.
           */}
          <div className="w-full flex overflow-hidden" style={{ height: "clamp(420px, 68vh, 650px)" }}>

            {/* ── Left sidebar ── */}
            <div
              className="w-40 xl:w-56 2xl:w-60 bg-[#f5ede3] border-r border-[#e8ddd4] flex flex-col flex-shrink-0 overflow-hidden"
              style={{ height: "clamp(420px, 68vh, 650px)" }}
            >
              <div className="px-4 xl:px-7 pt-6 xl:pt-9 pb-4 xl:pb-5 flex-shrink-0">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-3 h-3 text-[#c9a96e]" />
                  <span className="text-[10px] font-medium tracking-[0.2em] uppercase text-[#c9a96e]" style={{ fontFamily: "'Jost', sans-serif" }}>
                    Collections
                  </span>
                </div>
                <div className="h-px bg-gradient-to-r from-[#c9a96e] to-transparent mt-3" />
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-3 xl:px-5 pb-8 scrollbar-luxury">
                <ul className="space-y-0.5">
                  {categories.map((category) => (
                    <li key={category.id}>
                      <NavLink
                        href={getCategoryHref(category)}
                        onMouseEnter={() => handleCategoryHover(category)}
                        onClick={() => handleCategoryClick(category)}
                        className={`cat-pill block w-full text-left pl-3 xl:pl-5 pr-2 xl:pr-4 py-2 xl:py-2.5 rounded-sm text-xs xl:text-sm transition-all duration-200 ${
                          displayCategory?.id === category.id
                            ? "active bg-white text-[#1a1410] shadow-sm font-medium"
                            : "text-[#6b6560] hover:bg-white/60 hover:text-[#1a1410]"
                        }`}
                        style={{ fontFamily: "'Jost', sans-serif", letterSpacing: "0.02em" }}
                      >
                        {category.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-4 xl:px-7 py-4 xl:py-5 flex-shrink-0 border-t border-[#e8ddd4]">
                <div className="flex items-center gap-2 opacity-40">
                  <div className="flex-1 h-px bg-[#c9a96e]" />
                  <span className="text-[#c9a96e] text-xs">◆</span>
                  <div className="flex-1 h-px bg-[#c9a96e]" />
                </div>
              </div>
            </div>

            {/* ── Center: subcategory columns (xl+) ──
             *  min-w-0 is critical: without it, a flex child's min-width
             *  defaults to auto (= its content width) and the hero image
             *  panel can squeeze this column until text overflows into it.
             *  overflow-hidden clips anything that tries to escape the box.
             *  isolation:isolate creates a fresh stacking context so the
             *  image panel's z-index can never paint over this column.
             */}
            <div
              className="hidden xl:flex flex-1 min-w-0 flex-col px-10 py-9 overflow-hidden"
              style={{ isolation: "isolate" }}
            >
              <div className="flex items-end justify-between mb-7 flex-shrink-0 gap-2">
                <div className="min-w-0">
                  <h2 className="mega-menu-display text-3xl font-light text-[#1a1410] leading-none truncate" style={{ letterSpacing: "-0.01em" }}>
                    {displayCategory?.name}
                  </h2>
                  <div className="h-px w-16 bg-[#c9a96e] mt-3" />
                </div>
                <NavLink href={getViewAllHref(displayCategory)} onClick={handleViewAllClick} className="view-all-link flex-shrink-0">
                  View All <ArrowRight className="w-3 h-3" />
                </NavLink>
              </div>

              <div className="flex-1 min-h-0 overflow-hidden">
                <div className="grid grid-cols-3 gap-8 h-full">
                  {[col1, col2, col3].map((col, colIdx) => (
                    <div key={colIdx} className="flex flex-col overflow-hidden">
                      {colIdx === 0 && (
                        <>
                          <span className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#c9a96e] mb-3" style={{ fontFamily: "'Jost', sans-serif" }}>
                            Browse
                          </span>
                          <div className="subcol-divider" />
                        </>
                      )}
                      {colIdx > 0 && <div className="mb-3 mt-0" style={{ height: "29px" }} />}
                      <div className="overflow-y-auto scrollbar-luxury flex-1 min-h-0">
                        <ul className="space-y-0.5">
                          {col.length > 0
                            ? col.map((subCat) => (
                                <SubCategoryLink
                                  key={subCat.id}
                                  subCat={subCat}
                                  href={getSubCategoryHref(displayCategory.id, subCat)}
                                  onMouseEnter={() => setHoveredSubCategory(subCat)}
                                  onMouseLeave={() => setHoveredSubCategory(null)}
                                  onClick={() => handleSubCategoryClick(subCat)}
                                />
                              ))
                            : colIdx === 0 && (
                                <li className="text-sm text-[#b0a89e] italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                                  No subcategories
                                </li>
                              )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Tablet center: image grid (lg only) ──
                KEY FIX: isolated from hover state via CSS contain, lazy images,
                stable callbacks, and memoized SubCategoryCard components        */}
            <div
              className="flex xl:hidden flex-1 min-w-0 flex-col overflow-hidden border-l border-[#e8ddd4] bg-white"
              style={{ height: "clamp(420px, 68vh, 650px)" }}
            >
              <div className="px-6 pt-6 pb-4 flex-shrink-0 flex items-end justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="mega-menu-display text-2xl font-light text-[#1a1410] leading-none truncate" style={{ letterSpacing: "-0.01em" }}>
                    {displayCategory?.name}
                  </h2>
                  <div className="h-px w-12 bg-[#c9a96e] mt-2" />
                </div>
                <NavLink href={getViewAllHref(displayCategory)} onClick={handleViewAllClick} className="view-all-link flex-shrink-0 text-[10px] px-3 py-2">
                  View All <ArrowRight className="w-3 h-3" />
                </NavLink>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 scrollbar-luxury">
                <div className="grid grid-cols-3 gap-2">
                  {allSubCategories.map((subCat) => (
                    <SubCategoryCard
                      key={subCat.id}
                      subCat={subCat}
                      href={getSubCategoryHref(displayCategory.id, subCat)}
                      imgUrl={tabletImageUrls[subCat.id]}
                      onClick={() => handleSubCategoryClick(subCat)}
                    />
                  ))}
                </div>
              </div>
            </div>


            <div
              className="hidden xl:block flex-shrink-0 relative overflow-hidden "
              style={{
                width: "clamp(260px, 26%, 420px)",
                height: "clamp(400px, 68vh, 650px)",
                isolation: "isolate",
              }}
            >
              {/* Image is absolutely positioned so it is hard-clipped by the
                  parent's overflow:hidden and can never escape its bounds */}
              <img
                src={heroImage}
                alt={heroTitle}
                onLoad={() => setImageLoaded(true)}
                className="hero-img absolute inset-0 w-full h-full object-contain object-center"
                style={{
                  opacity: imageLoaded ? 1 : 0,
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              />

              {/* Gradient overlay — sits above the image, below the text */}
              <div className="absolute inset-0 z-10 pointer-events-none"

              />

              {/* Text / CTA — always above image and overlay */}
              <div className="absolute bottom-0 left-0 right-0 z-20 p-6 xl:p-8">
                <div className="h-px bg-gradient-to-r from-[#c9a96e]/60 to-transparent mb-4" />
                <span className="block text-[10px] tracking-[0.22em] uppercase text-[#c9a96e] mb-2" style={{ fontFamily: "'Jost', sans-serif" }}>
                  {hoveredSubCategory ? "Subcollection" : "Collection"}
                </span>
                <NavLink
                  href={heroLink}
                  onClick={handleHeroClick}
                  className="mega-menu-display text-[#c9a96e] text-xl xl:text-2xl font-light leading-tight text-left block w-full"
                >
                  {heroTitle}
                </NavLink>
                <NavLink
                  href={heroLink}
                  onClick={handleHeroClick}
                  className="mt-4 flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-white/70 hover:text-[#c9a96e] transition-colors group"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </NavLink>
              </div>

              {/* Decorative diamond */}
              <div className="absolute top-6 right-6 z-20 opacity-50">
                <div className="w-8 h-8 border border-[#c9a96e]/40 rotate-45" />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ════════════════ MOBILE ════════════════ */}
      <div className="lg:hidden w-full bg-[#faf7f4] shadow-2xl z-40 max-h-[85vh] overflow-y-auto" style={{ fontFamily: "'Jost', sans-serif" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500&family=Jost:wght@300;400;500&display=swap');`}</style>

        <div className="relative">
          <button
            onClick={onNavigate}
            className="absolute top-4 right-4 p-2 z-30 bg-white/80 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
          >
            <X className="w-4 h-4 text-[#1a1410]" />
          </button>

          {/* Mobile hero — overflow:hidden clips the image strictly */}
          <div className="relative h-52 overflow-hidden bg-[#1a1410]">
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1410]/75 via-[#1a1410]/20 to-transparent z-10" />
            <img
              src={heroImage}
              alt={heroTitle}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute bottom-5 left-5 z-20">
              <div className="h-px w-10 bg-[#c9a96e] mb-2" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#c9a96e]">Collection</span>
              <NavLink
                href={heroLink}
                onClick={handleHeroClick}
                className="block text-white text-xl font-light mt-1 hover:text-[#c9a96e] transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                {heroTitle}
              </NavLink>
            </div>
          </div>

          <div className="px-5 py-4 bg-[#f5ede3] border-b border-[#e8ddd4]">
            <NavLink
              href={getViewAllHref(displayCategory)}
              onClick={handleViewAllClick}
              className="w-full py-3 px-4 bg-[#1a1410] text-[#f5ede3] text-[11px] font-medium tracking-[0.18em] uppercase hover:bg-[#2d2320] transition-colors flex items-center justify-center gap-2"
            >
              View All {displayCategory?.name}
              <ArrowRight className="w-3.5 h-3.5" />
            </NavLink>
          </div>

          <div className="px-5 py-3">
            {categories.map((category) => {
              const subs = category.sub_categories ?? [];
              const isExpanded = expandedCategory === category.id;

              return (
                <div key={category.id} className="border-b border-[#e8ddd4] last:border-b-0">
                  <div className="flex items-center justify-between py-4">
                    <NavLink
                      href={getCategoryHref(category)}
                      onClick={() => handleCategoryClick(category)}
                      className="flex-1 text-left font-medium text-[#1a1410] text-base tracking-wide"
                    >
                      {category.name}
                    </NavLink>
                    {subs.length > 0 && (
                      <button
                        onClick={() => toggleCategoryExpand(category.id)}
                        className="p-2 rounded-full hover:bg-[#e8ddd4] transition-colors"
                      >
                        <ChevronRight className={`w-4 h-4 text-[#c9a96e] transition-transform duration-300 ${isExpanded ? "rotate-90" : ""}`} />
                      </button>
                    )}
                  </div>

                  {isExpanded && subs.length > 0 && (
                    <div className="pb-4 pl-4 space-y-1">
                      {subs.map((subCat) => (
                        <NavLink
                          key={subCat.id}
                          href={getSubCategoryHref(category.id, subCat)}
                          onClick={() => navigate(`/shop/${category.id}/${subCat.id}`)}
                          className="flex items-center gap-2 w-full text-left py-2 px-3 text-sm text-[#6b6560] hover:text-[#1a1410] hover:bg-[#f5ede3] rounded-sm transition-all group"
                        >
                          <span className="w-3 h-px bg-[#c9a96e] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          {subCat.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {allSubCategories.length > 0 && (
            <div className="px-5 py-6 bg-[#f5ede3] border-t border-[#e8ddd4]">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#c9a96e]">Quick Access</span>
                <div className="flex-1 h-px bg-[#e8ddd4]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {allSubCategories.slice(0, 6).map((subCat) => (
                  <NavLink
                    key={subCat.id}
                    href={getSubCategoryHref(displayCategory.id, subCat)}
                    onClick={() => handleSubCategoryClick(subCat)}
                    className="block py-2.5 px-3 text-xs text-left text-[#6b6560] bg-white border border-[#e8ddd4] hover:border-[#c9a96e] hover:text-[#1a1410] transition-all duration-200"
                  >
                    {subCat.name}
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}